// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomEvent } from '@shared/utils/CustomEvents';
import TerceraTabStripTemplate from '@front/templates/TerceraTabStripTemplate.html';
import { ContainerControl } from './ContainerControl';
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { getMenuHeight, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { throttle } from 'lodash';
import { findVisibleTabRange } from '../UtilsClasses/TerceraTabStripUtils';
export class TerceraTabStrip extends ContainerControl {
    constructor() {
        super();
        this.onTabRemove = null;
        this.onTabCreate = null;
        this.onTabLock = null;
        this.onTabRemovePrepare = null;
        this.updateView = () => {
            const validationResult = this.validateAndPrepareUpdate();
            if (validationResult === undefined) {
                void this.set({ showMoreItem: false });
                return;
            }
            const { items, selectedItemIndex, root, tabItems, availableWidth } = validationResult;
            const usedWidth = this.calculateUsedWidth(root);
            const { startIndex, endIndex } = findVisibleTabRange(Array.from(tabItems), availableWidth - usedWidth, selectedItemIndex, TerceraTabStrip.GAP, 1);
            this.updateTabVisibility(items, startIndex, endIndex);
            void this.set({ items, showMoreItem: startIndex !== 0 || endIndex !== tabItems.length - 1 });
        };
        this.throttledResizeWindow = throttle(() => __awaiter(this, void 0, void 0, function* () {
            yield this.showAllTabs();
            this.updateView();
        }), TerceraTabStrip.RESIZE_THROTTLE_MS, { leading: true, trailing: true });
    }
    getType() {
        return 'TerceraTabManagerRactive';
    }
    oninit() {
        super.oninit();
        this.on('tabStripItem', this.tabStripItemChange);
        this.on('tabStripNewItem', this.createNewTabStripItem);
        this.on('tabStripDeleteItem', this.deletTabStripItemPrepare);
        this.on('tabStripLockItem', this.lockTabStripItem);
        this.on('tabStripDoubleClick', this.tabStripDoubleClick);
        this.onTabRemove = new CustomEvent();
        this.onTabCreate = new CustomEvent();
        this.onTabLock = new CustomEvent();
        this.onTabRemovePrepare = new CustomEvent();
        this.onTabStripDoubleClick = new CustomEvent();
        this.observe('selectedItem', this.updateView, { init: false, defer: true });
    }
    deletTabStripItemPrepare(context, index) {
        if (!this.get('isCanChangeWorkspaces'))
            return;
        if (this.onTabRemovePrepare.Subscribers.length < 0) {
            this.deletTabStripItem(event, index, true);
        }
        else {
            const items = this.get('items');
            const curItem = items[index];
            this.onTabRemovePrepare.Raise(this.deletTabStripItem.bind(this, context, index, true), curItem.data);
        }
    }
    deletTabStripItem(context, index, skipConfirm) {
        const items = this.get('items');
        const activeItem = this.get('selectedItem');
        const curItem = items[index];
        items.splice(index, 1);
        if (curItem === activeItem && items.length !== 0) {
            let newIndex = index;
            if (index !== 0 && items.length >= index) {
                newIndex = index - 1;
            }
            const newActiveItem = items[newIndex];
            newActiveItem.active = true;
            void this.set({ items, selectedItem: newActiveItem });
        }
        else {
            void this.set({ items });
        }
        this.onTabRemove.Raise(curItem.data, skipConfirm);
    }
    lockTabStripItem(context, index) {
        if (!this.get('isCanChangeWorkspaces'))
            return;
        const activeItem = this.get('selectedItem');
        const items = this.get('items');
        activeItem.locked = !activeItem.locked;
        this.onTabLock.Raise(items[index].data);
    }
    tabStripItemChange(context, index) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.get('isCanChangeWorkspaces'))
                return;
            const items = this.get('items');
            const selectedItem = this.get('selectedItem');
            const newSelectedItem = items[index];
            if (newSelectedItem === selectedItem || !newSelectedItem.enabled) {
                return;
            }
            if (!isNullOrUndefined(selectedItem)) {
                selectedItem.active = false;
            }
            if (!isNullOrUndefined(this.onTabChangeAsync)) {
                yield this.onTabChangeAsync(newSelectedItem.data);
            }
        });
    }
    tabStripShowMoreActionMenu(context, _index) {
        var _a, _b;
        if (Resources.isHidden('allowWorkspaceContextMenu') || !this.get('isCanChangeWorkspaces')) {
            return;
        }
        const { X, Y } = context.component.getAbsoluteRectangle();
        const items = MainWindowManager.MainWindow.getWorkspaceContextMenu();
        const menuHeight = getMenuHeight(items.length);
        showContextMenu({
            x: X,
            y: Y - menuHeight - 1,
            items: items,
            functions: {
                closeCallback: () => {
                    var _a, _b;
                    (_b = (_a = context.component) === null || _a === void 0 ? void 0 : _a.lostFocus) === null || _b === void 0 ? void 0 : _b.call(_a);
                }
            }
        });
        (_b = (_a = context.component) === null || _a === void 0 ? void 0 : _a.gotFocus) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    tabStripDoubleClick(context, index) {
        if (!this.get('isCanChangeWorkspaces'))
            return;
        this.onTabStripDoubleClick.Raise(context);
    }
    createNewTabStripItem(context, index) {
        const selectedItem = this.get('selectedItem');
        if (!isNullOrUndefined(selectedItem)) {
            selectedItem.active = false;
        }
        const items = this.get('items');
        const maxElementCount = this.get('maxElementCount');
        if (maxElementCount !== -1 && items.length >= maxElementCount) {
            this.fire('maxElementsCountReached');
            return;
        }
        const intitValue = new TerceraTabStripItem();
        intitValue.active = true;
        items.push(intitValue);
        void this.set({ items, selectedItem: items[items.length - 1] });
        this.onTabCreate.Raise();
    }
    showMoreTabs(context, _index) {
        const items = this.get('items');
        const len = items.length;
        const hiddenArray = [];
        for (let i = 0; i < len; i++) {
            if (!items[i].visible) {
                hiddenArray.push({
                    type: NewContextMenuItemType.Default,
                    text: items[i].text,
                    value: i,
                    event: (item) => {
                        this.showMoreContextMenuItemSelect(item);
                    }
                });
            }
        }
        const btn = this.find('.tabStrip-ShowMoreTabs');
        const btnRect = btn.getBoundingClientRect();
        const menuHeight = getMenuHeight(hiddenArray.length);
        showContextMenu({
            x: btnRect.left,
            y: btnRect.top - menuHeight - 1,
            items: hiddenArray,
            functions: {
                closeCallback: () => {
                    void this.set('isMoreMenuOpen', false);
                }
            }
        });
        void this.set('isMoreMenuOpen', true);
    }
    showMoreContextMenuItemSelect(item) {
        void this.tabStripItemChange(null, item.value);
    }
    selectItem(data) {
        const items = this.get('items');
        const len = items.length;
        for (let i = 0; i < len; i++) {
            const curItem = items[i];
            if (curItem.data === data) {
                curItem.active = true;
                void this.set({ selectedItem: curItem });
                break;
            }
        }
    }
    setLockedStateForSelectedItem(locked) {
        const selectedItem = this.get('selectedItem');
        const items = this.get('items');
        if (isNullOrUndefined(selectedItem) || !isValidArray(items)) {
            return;
        }
        const selectedItemIdx = items.indexOf(selectedItem);
        if (selectedItemIdx === -1)
            return;
        void this.set('items.' + selectedItemIdx + '.locked', locked);
    }
    setTabNameByData(searchData, newName) {
        return __awaiter(this, void 0, void 0, function* () {
            const items = this.get('items');
            const len = items.length;
            for (let i = 0; i < len; i++) {
                const item = items[i];
                if (item.hasOwnProperty('data') && item.data === searchData) {
                    yield this.set('items.' + i + '.text', newName);
                    yield this.showAllTabs();
                    this.updateView();
                    return;
                }
            }
        });
    }
    oncomplete() {
        super.oncomplete();
        const items = this.get('items');
        if (isValidArray(items)) {
            void this.set({ selectedItem: items[0] });
        }
        window.addEventListener('resize', this.throttledResizeWindow);
    }
    dispose() {
        window.removeEventListener('resize', this.throttledResizeWindow);
    }
    getActiveElementSizeParams() {
        var _a, _b, _c, _d, _e;
        const strip = this.find('div');
        const items = Array.from(strip.querySelectorAll('.tabStrip-item'));
        for (const el of items) {
            if (!el.hasAttribute('active'))
                continue;
            const rect = el.getBoundingClientRect();
            const doc = el.ownerDocument.documentElement;
            const win = (_a = el.ownerDocument.defaultView) !== null && _a !== void 0 ? _a : window;
            const left = rect.left + ((_b = win.pageXOffset) !== null && _b !== void 0 ? _b : 0) - ((_c = doc.clientLeft) !== null && _c !== void 0 ? _c : 0);
            const top = rect.top + ((_d = win.pageYOffset) !== null && _d !== void 0 ? _d : 0) - ((_e = doc.clientTop) !== null && _e !== void 0 ? _e : 0);
            return { offset: { left, top }, width: rect.width, height: rect.height };
        }
        return { offset: { left: 0, top: 0 }, width: 0, height: 0 };
    }
    setPoweredByTextAndLink(text, url) {
        void this.set({
            poweredByText: text,
            poweredByVisible: isValidString(text),
            poweredByURL: url
        });
    }
    validateAndPrepareUpdate() {
        if (!isValidArray(this.get('items'))) {
            return undefined;
        }
        const items = this.get('items');
        const selectedItem = this.get('selectedItem');
        const selectedItemIndex = items.indexOf(selectedItem);
        const root = this.find('*');
        const tabItems = root.querySelectorAll('.tabStrip-item');
        const availableWidth = root.clientWidth;
        return { items, selectedItemIndex, root, tabItems, availableWidth };
    }
    calculateUsedWidth(root) {
        const px = (v) => { var _a; return (_a = Number.parseFloat(v)) !== null && _a !== void 0 ? _a : 0; };
        const tabStripStyles = getComputedStyle(root);
        let usedWidth = px(tabStripStyles.paddingLeft) + px(tabStripStyles.paddingRight);
        const gap = TerceraTabStrip.GAP;
        const btnsContainer = this.find('.tabStrip-buttons');
        const btnsContainerRect = btnsContainer === null || btnsContainer === void 0 ? void 0 : btnsContainer.getBoundingClientRect();
        usedWidth += btnsContainerRect.width;
        if (this.get('poweredByVisible')) {
            const poweredLinkLabel = this.find('.js-link-label');
            usedWidth += gap + poweredLinkLabel.offsetWidth;
        }
        return usedWidth;
    }
    showAllTabs() {
        return __awaiter(this, void 0, void 0, function* () {
            const items = this.get('items');
            for (const item of items) {
                item.visible = true;
            }
            yield this.set({ items });
        });
    }
    updateTabVisibility(items, startIndex, endIndex) {
        for (let i = 0; i < items.length; i++) {
            items[i].visible = startIndex <= i && i <= endIndex;
        }
    }
}
TerceraTabStrip.GAP = 4;
TerceraTabStrip.RESIZE_THROTTLE_MS = 150;
export class TerceraTabStripItem {
    constructor() {
        this.text = '';
        this.data = '';
        this.locked = false;
        this.visible = true;
        this.active = false;
        this.enabled = true;
    }
}
ContainerControl.extendWith(TerceraTabStrip, {
    template: TerceraTabStripTemplate,
    data: function () {
        return {
            items: [],
            selectedItem: null,
            maxElementCount: -1,
            allowUserEdit: true,
            allowMoreActionMenu: false,
            allowCloseTab: true,
            allowAddNewTab: true,
            showMoreItem: false /* todo to false */,
            isMoreMenuOpen: false,
            poweredByText: null, // #88323
            poweredByURL: null,
            poweredByVisible: false,
            isCanChangeWorkspaces: false
        };
    }
});
